import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/lib/cn'

import { IconButton } from './Button'

/** Общая основа Modal и Sheet: портал, затемнение, Escape, фокус внутрь и обратно.
 * `focusPanel` — фокус на само окно, а не на первое поле: на телефоне не выезжает клавиатура. */
function useDialog(open: boolean, onClose: () => void, focusPanel = false) {
  const panelRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!open) return
    const previous = document.activeElement as HTMLElement | null
    const panel = panelRef.current
    const focusable = focusPanel
      ? null
      : panel?.querySelector<HTMLElement>(
          '[autofocus], input, select, textarea, button:not([data-dialog-close]), [href], [tabindex]:not([tabindex="-1"])',
        )
    ;(focusable ?? panel)?.focus()
    const onKey = (e: KeyboardEvent) => {
      // A confirmation can open above a drawer. Only the top dialog handles keys.
      const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]')
      if (dialogs[dialogs.length - 1] !== panel) return
      if (e.key === 'Escape') {
        e.preventDefault()
        onCloseRef.current()
      }
      if (e.key === 'Tab' && panel) {
        const controls = [...panel.querySelectorAll<HTMLElement>('input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')]
          .filter((item) => !item.hidden && item.getAttribute('type') !== 'hidden' && !item.closest('[hidden], [aria-hidden="true"]'))
        const first = controls[0]
        const last = controls[controls.length - 1]
        if (!first) { e.preventDefault(); panel.focus() }
        else if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
          e.preventDefault(); last?.focus()
        } else if (!e.shiftKey && (document.activeElement === last || document.activeElement === panel)) {
          e.preventDefault(); first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
      previous?.focus?.()
    }
  }, [open, focusPanel])

  return panelRef
}

type DialogProps = {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  className?: string
  /** Не ставить фокус в первое поле (на телефоне откроется клавиатура) — только на окно. */
  focusPanel?: boolean
}

/** Диалог по центру: подтверждения и короткие формы. */
export function Modal({ open, onClose, title, description, children, footer, className, focusPanel }: DialogProps) {
  const panelRef = useDialog(open, onClose, focusPanel)
  const titleId = useId()
  if (!open) return null
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'relative flex max-h-[92dvh] w-full flex-col rounded-t-2xl border border-border bg-surface shadow-xl',
          'pb-[env(safe-area-inset-bottom)] sm:max-w-md sm:rounded-2xl sm:pb-0',
          className,
        )}
      >
        <div className="flex items-start gap-3 px-5 pb-2 pt-4">
          <div className="min-w-0 flex-1 pt-2">
            <h2 id={titleId} className="text-base font-semibold text-fg">
              {title}
            </h2>
            {description ? <p className="mt-1 text-sm text-muted">{description}</p> : null}
          </div>
          <IconButton icon="close" label="Закрыть" onClick={onClose} data-dialog-close />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">{children}</div>
        {footer ? (
          <div className="flex flex-col-reverse gap-2 border-t border-border px-5 py-3 sm:flex-row sm:justify-end">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}

/** Панель снизу на телефоне и справа на десктопе: длинные формы, выбор из списка.
 *  `fullMobile` — на телефоне на весь экран (длинный текст для чтения);
 *  `wide` — на десктопе шире (формы с рядами кнопок, длинный текст). */
export function Sheet({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  className,
  fullMobile = false,
  wide = false,
}: DialogProps & { fullMobile?: boolean; wide?: boolean }) {
  const panelRef = useDialog(open, onClose)
  const titleId = useId()
  if (!open) return null
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end lg:items-stretch lg:justify-end">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'relative flex w-full flex-col border-border bg-surface shadow-xl',
          fullMobile
            ? 'h-dvh pt-[env(safe-area-inset-top)] lg:h-auto lg:border-l lg:pt-0'
            : 'max-h-[92dvh] rounded-t-2xl border lg:border-y-0 lg:border-r-0',
          wide ? 'lg:w-[min(720px,90vw)]' : 'lg:w-[420px]',
          'pb-[env(safe-area-inset-bottom)] lg:max-h-none lg:rounded-none lg:pb-0',
          className,
        )}
      >
        {fullMobile ? null : (
          <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-border lg:hidden" aria-hidden="true" />
        )}
        <div className="flex items-start gap-3 px-5 pb-2 pt-3">
          <div className="min-w-0 flex-1 pt-2">
            <h2 id={titleId} className="text-base font-semibold text-fg">
              {title}
            </h2>
            {description ? <p className="mt-1 text-sm text-muted">{description}</p> : null}
          </div>
          <IconButton icon="close" label="Закрыть" onClick={onClose} data-dialog-close />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">{children}</div>
        {footer ? (
          <div className="flex flex-col-reverse gap-2 border-t border-border px-5 py-3 sm:flex-row sm:justify-end">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
